import { useCallback, useEffect, useRef, useState } from 'react';
import { api, ApiError, type Account, type Company, type NewCompany,
  LEGAL_FORMS,
  personTaxed,
} from '../api';
import type { Dictionary, Locale } from '../i18n';
import { initials, nip as formatNip } from '../format';
import { roleLabel } from '../gender';
import * as Icon from '../components/Icons';
import { Shell, TopControls, useDensity, type Panel } from '../components/Shell';
import { AccessPanel } from './Access';
import { AssetsPanel } from './Assets';
import { BankAccountsPanel } from './BankAccounts';
import { BillingPanel } from './Billing';
import { NumberingPanel, ProductsPanel } from './Catalog';
import { ChartPanel } from './Chart';
import { JournalPanel } from './Journal';
import { ReportsPanel } from './Reports';
import { CostsPanel } from './Costs';
import { DocumentsPanel, IntakePanel } from './Documents';
import { useCompanyDocuments } from '../notifications/useCompanyDocuments';
import { useDocumentSound } from '../notifications/useDocumentSound';
import { DocumentSoundControl } from '../notifications/DocumentSoundControl';
import { PeriodsPanel } from './Periods';
import { AnnualPanel } from './Annual';
import { PartnersPanel } from '../components/PartnersPanel';
import { SuspensionsPanel } from '../components/Suspensions';
import { CounterpartiesPanel } from './Counterparties';
import { DashboardPanel } from './Dashboard';
import { LedgerPanel } from './Ledger';
import { RyczaltEvidencePanel } from './RyczaltEvidence';
import { SettlementsPanel } from './Settlements';
import { InventoryPanel } from './Inventory';
import { InvoicesPanel } from './Invoices';
import { CompanySettingsPanel } from './Settings';
import { FilingDetailsPanel } from './FilingDetails';

const emptyForm: NewCompany = { nip: '', legalForm: 'JDG', name: '', addressLine: '', postalCode: '', city: '' };

/**
 * Kto będzie prowadzić księgi. Pytamy o to przy zakładaniu firmy, a nie dopiero wtedy, gdy ktoś
 * utknie: przy pełnej księgowości księguje wyłącznie rola Księgowa, więc spółka założona bez
 * odpowiedzi na to pytanie stoi w miejscu, nie wiedząc dlaczego.
 *
 * Odpowiedzi nie zapisujemy. Prawdziwy stan wynika z ról i umów z biurem, a druga kopia tej samej
 * informacji rozjechałaby się z nią pierwszego dnia. Wybór decyduje tylko o tym, gdzie trafiasz
 * po założeniu firmy.
 */
type Bookkeeping = 'SELF' | 'ACCOUNTANT' | 'OFFICE';

/**
 * Firma, w którą wchodzimy od razu, z pominięciem listy.
 *
 * Przy jednej firmie „Moje firmy" to ekran z jednym kafelkiem — klikanie w niego po każdym
 * zalogowaniu jest krokiem donikąd. Wchodzimy więc prosto w pulpit, ale **tylko przy pierwszym
 * wczytaniu listy**: kto z niej wyszedł (żeby dodać drugą firmę albo zajrzeć do biura), ma na niej
 * zostać. Bez tego warunku okruszek „Moje firmy" byłby ślepy — wrzucałby z powrotem tam, skąd
 * człowiek właśnie wyszedł.
 */
export function companyToEnterDirectly(companies: Company[] | null, alreadyEntered: boolean): Company | null {
  if (alreadyEntered || companies === null || companies.length !== 1) return null;
  return companies[0];
}

type Props = {
  t: Dictionary;
  locale: Locale;
  account: Account;
  /** Firma wzięta z adresu; `null` znaczy listę firm. Ekran idzie za adresem, nie odwrotnie. */
  companyId: string | null;
  panel: Panel;
  onOpen: (companyId: string, panel: Panel) => void;
  onLeave: () => void;
  onLocale: (next: Locale) => void;
  onOffice: () => void;
  onSignOut: () => void;
};

export function Companies({
  t,
  locale,
  account,
  companyId,
  panel,
  onOpen,
  onLeave,
  onLocale,
  onOffice,
  onSignOut,
}: Props) {
  const [companies, setCompanies] = useState<Company[] | null>(null);
  const [density, setDensity] = useDensity();

  const load = useCallback(async () => {
    setCompanies(await api.companies());
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  /*
   * Otwartą firmę wyszukujemy na świeżej liście przy każdym rysowaniu, zamiast trzymać jej kopię
   * w stanie. Kopia rozjeżdżała się z listą po zmianie nazwy i okruszki zaczynały kłamać.
   */
  const opened = companyId !== null && companies !== null
    ? companies.find((company) => company.id === companyId) ?? null
    : null;

  /*
   * Jedna firma — lista jest zbędnym klikiem; wchodzimy w nią raz, przy pierwszym wczytaniu.
   * Firma w adresie znaczy, że wchodzić nie trzeba: ktoś przyszedł z zakładki albo z linku.
   */
  const entered = useRef(false);
  useEffect(() => {
    if (companies === null) return;
    const only = companyId === null ? companyToEnterDirectly(companies, entered.current) : null;
    entered.current = true;
    if (only) onOpen(only.id, 'dashboard');
  }, [companies, companyId, onOpen]);

  /*
   * Adres wskazuje firmę, której nie ma na liście: cudzą, zamkniętą albo z literówki.
   * Wracamy na listę — pusty ekran pod adresem, który nic nie znaczy, jest gorszy niż przeniesienie.
   */
  useEffect(() => {
    if (companyId === null || companies === null) return;
    if (!companies.some((company) => company.id === companyId)) onLeave();
  }, [companyId, companies, onLeave]);

  if (companyId !== null) {
    if (!opened) return <p className="muted">{t.loading}</p>;
    return (
      <CompanyWorkspace
        company={opened}
        panel={panel}
        onPanel={(next) => onOpen(opened.id, next)}
        onLeave={onLeave}
        onChanged={() => void load()}
        t={t}
        locale={locale}
        account={account}
        density={density}
        onDensity={setDensity}
        onLocale={onLocale}
      />
    );
  }

  return (
    <CompanyList
      companies={companies}
      onOpen={(company, panel) => onOpen(company.id, panel ?? 'dashboard')}
      onCreated={load}
      t={t}
      locale={locale}
      account={account}
      density={density}
      onDensity={setDensity}
      onLocale={onLocale}
      onOffice={onOffice}
      onSignOut={onSignOut}
    />
  );
}

/**
 * Ekran 04: firmy jako karty, nie jako wiersze z osiemnastoma przyciskami.
 *
 * Rola jest etykietą przy firmie, a nie wierszem „Twoja rola: OWNER” pisanym wersalikami
 * prosto z bazy. Karta pokazuje to, po czym rozpoznaje się firmę na liście.
 */
function CompanyList({
  companies,
  onOpen,
  onCreated,
  t,
  locale,
  account,
  density,
  onDensity,
  onLocale,
  onOffice,
  onSignOut,
}: {
  companies: Company[] | null;
  onOpen: (company: Company, panel?: Panel) => void;
  onCreated: () => Promise<void>;
  t: Dictionary;
  locale: Locale;
  account: Account;
  density: 'comfortable' | 'dense';
  onDensity: (next: 'comfortable' | 'dense') => void;
  onLocale: (next: Locale) => void;
  onOffice: () => void;
  onSignOut: () => void;
}) {
  const [form, setForm] = useState<NewCompany | null>(null);
  const [bookkeeping, setBookkeeping] = useState<Bookkeeping>('SELF');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Skąd wzięły się wypełnione pola — człowiek ma wiedzieć, czemu ufa i co warto sprawdzić
  const [fetched, setFetched] = useState<string | null>(null);
  const [fetching, setFetching] = useState(false);

  /**
   * Dane z rejestru wypełniają formularz, ale go nie zatwierdzają.
   *
   * Forma prawna bierze się z pełnego brzmienia nazwy, którą podaje rejestr — polska nazwa
   * spółki **musi** zawierać oznaczenie formy, a serwer potwierdza je w KRS-ie. Daty rozpoczęcia
   * wykaz nie zna wcale, więc dla jednoosobowej działalności wpisuje się ją ręcznie.
   */
  async function fetchFromRegistry() {
    if (!form) return;
    setFetching(true);
    setError(null);
    setFetched(null);
    try {
      const found = await api.lookupCompany(form.nip);
      setForm({
        ...form,
        nip: found.nip,
        name: found.name || form.name,
        addressLine: found.street ?? form.addressLine,
        postalCode: found.postalCode ?? form.postalCode,
        city: found.city ?? form.city,
        legalForm: found.legalForm ?? form.legalForm,
        businessStartedOn: found.businessStartedOn ?? form.businessStartedOn,
      });
      setFetched(
        found.vatStatus
          ? t.companies.fetchedWithVat(found.source, found.vatStatus)
          : t.companies.fetched(found.source),
      );
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.message : String(failure));
    } finally {
      setFetching(false);
    }
  }

  async function save(event: React.FormEvent) {
    event.preventDefault();
    if (!form) return;
    setSaving(true);
    setError(null);
    try {
      // Puste pola adresu wysyłamy jako brak wartości, a nie pusty tekst
      const trimmed = Object.fromEntries(
        Object.entries(form).map(([key, value]) => [
          key,
          typeof value === 'string' && !value.trim() ? undefined : value,
        ]),
      ) as NewCompany;
      const created = await api.createCompany(trimmed);
      setForm(null);
      await onCreated();
      // Przy księgowej i przy pełnych księgach następny krok jest na ekranie Osoby: tam zaprasza
      // się księgową i tam właściciel bierze tę rolę dla siebie
      const next = bookkeeping === 'OFFICE' ? 'dashboard' : 'access';
      onOpen(created, bookkeeping === 'SELF' && personTaxed(created.legalForm) ? 'dashboard' : next);
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.message : String(failure));
    } finally {
      setSaving(false);
    }
  }

  const legalFormName = (value: Company['legalForm']) => t.companies.forms[value] ?? value;
  const roleName = (role: string) => roleLabel(t, role, account.fullName);

  return (
    <div className="app solo">
      <div className="main">
        <div className="top">
          <div className="crumb">
            <b>{t.companies.title}</b>
          </div>
          <TopControls t={t} locale={locale} density={density} onDensity={onDensity} onLocale={onLocale}>
            <button type="button" className="btn sec sm" onClick={onOffice}>
              {t.office.tab}
            </button>
            <span className="row" style={{ gap: 8 }}>
              <span className="sm">{account.fullName}</span>
              <button type="button" className="btn ghost sm" onClick={onSignOut}>
                {t.signOut}
              </button>
            </span>
          </TopControls>
        </div>

        <div className="body">
          <div className="hd">
            <div>
              <h3>{t.companies.title}</h3>
              <p>{t.companies.subtitle}</p>
            </div>
            {!form && (
              <div className="sp">
                <button type="button" className="btn" onClick={() => setForm(emptyForm)}>
                  <Icon.Plus />
                  {t.companies.add}
                </button>
              </div>
            )}
          </div>

          {error && <div className="alert a-bad"><Icon.Error /><div><b>{error}</b></div></div>}

          {form && (
            <form className="panel" onSubmit={save}>
              <header>
                <h4>{t.companies.add}</h4>
              </header>
              <div className="fields">
                <div className="field">
                  <label htmlFor="nip">{t.companies.nip}</label>
                  <div className="row" style={{ gap: 8, flexWrap: 'nowrap' }}>
                    <input
                      id="nip"
                      className="inp mono"
                      value={form.nip}
                      onChange={(event) => {
                        setForm({ ...form, nip: event.target.value });
                        setFetched(null);
                      }}
                      inputMode="numeric"
                      autoComplete="off"
                      required
                    />
                    <button
                      type="button"
                      className="btn sec"
                      // Dziesięć cyfr to warunek konieczny — bez nich rejestr i tak odmówi
                      disabled={form.nip.replace(/[^0-9]/g, '').length !== 10 || fetching}
                      onClick={() => void fetchFromRegistry()}
                    >
                      {fetching ? t.companies.fetching : t.companies.fetch}
                    </button>
                  </div>
                  {fetched && <span className="hint">{fetched}</span>}
                </div>
                <div className="field">
                  <label htmlFor="name">{t.companies.name}</label>
                  <input
                    id="name"
                    className="inp"
                    value={form.name}
                    onChange={(event) => setForm({ ...form, name: event.target.value })}
                    required
                  />
                </div>
                <div className="field">
                  <label htmlFor="legalForm">{t.companies.legalForm}</label>
                  <select
                    id="legalForm"
                    className="inp"
                    value={form.legalForm}
                    onChange={(event) => setForm({ ...form, legalForm: event.target.value as Company['legalForm'] })}
                  >
                    {LEGAL_FORMS.map((code) => (
                      <option key={code} value={code}>
                        {t.companies.forms[code]}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="field">
                  <label htmlFor="started">{t.companies.businessStartedOn}</label>
                  <input
                    id="started"
                    className="inp"
                    type="date"
                    value={form.businessStartedOn ?? ''}
                    onChange={(event) => setForm({ ...form, businessStartedOn: event.target.value })}
                  />
                  <span className="hint">{t.companies.businessStartedOnHint}</span>
                </div>
                <div className="field">
                  <label htmlFor="street">{t.companies.addressLine}</label>
                  <input
                    id="street"
                    className="inp"
                    value={form.addressLine ?? ''}
                    onChange={(event) => setForm({ ...form, addressLine: event.target.value })}
                  />
                </div>
                <div className="field">
                  <label htmlFor="postal">{t.companies.postalCode}</label>
                  <input
                    id="postal"
                    className="inp"
                    value={form.postalCode ?? ''}
                    onChange={(event) => setForm({ ...form, postalCode: event.target.value })}
                  />
                </div>
                <div className="field">
                  <label htmlFor="city">{t.companies.city}</label>
                  <input
                    id="city"
                    className="inp"
                    value={form.city ?? ''}
                    onChange={(event) => setForm({ ...form, city: event.target.value })}
                  />
                </div>
              </div>
              <fieldset className="field">
                <legend>{t.companies.bookkeeping}</legend>
                <div className="row">
                  {(['SELF', 'ACCOUNTANT', 'OFFICE'] as const).map((choice) => (
                    <label className="check" key={choice}>
                      <input
                        type="radio"
                        name="bookkeeping"
                        checked={bookkeeping === choice}
                        onChange={() => setBookkeeping(choice)}
                      />
                      <span>{t.companies.bookkeepingChoices[choice]}</span>
                    </label>
                  ))}
                </div>
                <p className="sm muted">
                  {bookkeeping === 'SELF'
                    ? !personTaxed(form.legalForm)
                      ? t.companies.bookkeepingSelfFull
                      : t.companies.bookkeepingSelfSimple
                    : bookkeeping === 'ACCOUNTANT'
                      ? t.companies.bookkeepingAccountantHint
                      : t.companies.bookkeepingOfficeHint}
                </p>
              </fieldset>

              <div className="bar2">
                <button type="submit" className="btn" disabled={saving}>
                  {saving ? t.companies.saving : t.companies.save}
                </button>
                <button type="button" className="btn ghost" onClick={() => setForm(null)} disabled={saving}>
                  {t.companies.cancel}
                </button>
              </div>
            </form>
          )}

          {companies === null && <p className="muted">{t.loading}</p>}

          {companies !== null && (
            <ul className="firmy">
              {companies.map((company) => (
                <li key={company.id} style={{ display: 'flex' }}>
                  <button type="button" className="firma" onClick={() => onOpen(company)}>
                    <span className="nm">
                      <span className="av">{initials(company.name)}</span>
                      <span>
                        <b>{company.name}</b>
                        <span className="sm muted" style={{ display: 'block' }}>
                          {[legalFormName(company.legalForm), company.city].filter(Boolean).join(' · ')}
                        </span>
                      </span>
                    </span>
                    {/* Po tym rozpoznaje się firmę na liście: numer, forma rozliczenia, status VAT */}
                    <dl>
                      <dt>{t.companies.nip}</dt>
                      <dd className="mono">{formatNip(company.nip)}</dd>
                      {company.booksType && (
                        <>
                          <dt>{t.companies.settlement}</dt>
                          <dd>
                            {[
                              company.taxationForm &&
                                t.settings.forms[
                                  company.taxationForm.toLowerCase() as keyof typeof t.settings.forms
                                ],
                              t.settings.books[company.booksType.toLowerCase() as keyof typeof t.settings.books],
                            ]
                              .filter(Boolean)
                              .join(' · ')}
                          </dd>
                        </>
                      )}
                      {company.vatStatus && (
                        <>
                          <dt>{t.settings.vatStatus}</dt>
                          <dd>
                            {t.settings.vat[company.vatStatus.toLowerCase() as keyof typeof t.settings.vat]}
                          </dd>
                        </>
                      )}
                    </dl>
                    <span className="st">
                      {company.roles.map((role) => (
                        <span className="chip st-info" key={role}>
                          <i />
                          {t.companies.roles}: {roleName(role)}
                        </span>
                      ))}
                    </span>
                  </button>
                </li>
              ))}
              {!form && (
                <li style={{ display: 'flex' }}>
                  <button type="button" className="firma add" onClick={() => setForm(emptyForm)}>
                    <Icon.Plus />
                    <span>{t.companies.add}</span>
                    <span className="sm muted">{t.companies.addHint}</span>
                  </button>
                </li>
              )}
            </ul>
          )}

          {companies?.length === 0 && <p className="muted">{t.companies.empty}</p>}
        </div>
      </div>
    </div>
  );
}

/** Firma otwarta: powłoka z nawigacją, w środku jeden ekran naraz. */
function CompanyWorkspace({
  company,
  panel,
  onPanel,
  onLeave,
  onChanged,
  t,
  locale,
  account,
  density,
  onDensity,
  onLocale,
}: {
  company: Company;
  panel: Panel;
  onPanel: (next: Panel) => void;
  onLeave: () => void;
  /** Wołane, gdy ekran zmienił coś, co widać poza nim — np. rodzaj ksiąg w nawigacji. */
  onChanged: () => void;
  t: Dictionary;
  locale: Locale;
  account: Account;
  density: 'comfortable' | 'dense';
  onDensity: (next: 'comfortable' | 'dense') => void;
  onLocale: (next: Locale) => void;
}) {
  const roleName = (role: string) => roleLabel(t, role, account.fullName);
  const sound = useDocumentSound();
  const documentState = useCompanyDocuments(company.id, true, sound.play);

  return (
    <Shell
      badges={{ documents: documentState.pendingCount }}
      company={company}
      panel={panel}
      onPanel={onPanel}
      onLeave={onLeave}
      t={t}
      locale={locale}
      density={density}
      onDensity={onDensity}
      onLocale={onLocale}
      account={{ fullName: account.fullName, role: roleName(company.roles[0] ?? '') }}
    >
      <DocumentSoundControl sound={sound} t={t} />
      {panel === 'dashboard' && <DashboardPanel company={company} t={t} locale={locale} onGo={onPanel} />}
      {panel === 'invoices' && <InvoicesPanel company={company} t={t} locale={locale} onGo={onPanel} />}
      {panel === 'settlements' && <SettlementsPanel company={company} t={t} locale={locale} />}
      {panel === 'counterparties' && <CounterpartiesPanel company={company} t={t} />}
      {panel === 'products' && <ProductsPanel company={company} t={t} locale={locale} />}
      {panel === 'costs' && <CostsPanel company={company} t={t} locale={locale} onGo={onPanel} />}
      {panel === 'documents' && <DocumentsPanel company={company} t={t} locale={locale} onGo={onPanel} documentState={documentState} />}
      {panel === 'mailboxConfig' && <IntakePanel company={company} t={t} locale={locale} manager={company.roles.some((role) => role === 'OWNER' || role === 'ADMIN' || role === 'ACCOUNTANT')} />}
      {panel === 'chart' && <ChartPanel company={company} t={t} onGo={onPanel} />}
      {panel === 'journal' && (
        <JournalPanel company={company} t={t} locale={locale} onGo={onPanel} />
      )}
      {panel === 'reports' && <ReportsPanel company={company} t={t} locale={locale} onGo={onPanel} />}
      {panel === 'ledger' && <LedgerPanel company={company} t={t} locale={locale} onGo={onPanel} />}
      {panel === 'ryczalt' && <RyczaltEvidencePanel company={company} t={t} locale={locale} onGo={onPanel} />}
      {panel === 'assets' && <AssetsPanel company={company} t={t} locale={locale} onGo={onPanel} />}
      {panel === 'inventory' && <InventoryPanel company={company} t={t} locale={locale} />}
      {panel === 'periods' && <PeriodsPanel company={company} t={t} locale={locale} onGo={onPanel} />}
      {panel === 'annual' && <AnnualPanel company={company} t={t} locale={locale} onGo={onPanel} />}
      {panel === 'billing' && <BillingPanel company={company} t={t} locale={locale} onGo={onPanel} />}
      {/* Dane do deklaracji stoją obok ustawień podatkowych — obie rzeczy uzupełnia się raz */}
      {panel === 'settings' && (
        <>
          <CompanySettingsPanel company={company} t={t} locale={locale} onChanged={onChanged} />
          {/* Kartoteka wspólników dotyczy tylko spółek osobowych: tam podatek płaci człowiek,
              a nie jednostka, więc bez niej zaliczka jest liczbą, której nikt nie wpłaca */}
          {personTaxed(company.legalForm) && company.legalForm !== 'JDG' && (
            <PartnersPanel
              company={company}
              t={t}
              locale={locale}
              editable={company.roles.some((role) => role === 'OWNER' || role === 'ADMIN' || role === 'ACCOUNTANT')}
            />
          )}
          {/*
            Zawieszenie stoi w danych firmy, nie w ustawieniach podatkowych: to fakt z CEIDG, a nie
            wybór sposobu rozliczenia. Spółka na CIT też je ma — składek właściciela nie liczy, ale
            amortyzację i terminy zawieszenie zmienia tak samo.
          */}
          <SuspensionsPanel
            company={company}
            t={t}
            locale={locale}
            editable={company.roles.some((role) => role === 'OWNER' || role === 'ADMIN' || role === 'ACCOUNTANT')}
            onGo={onPanel}
          />
          <FilingDetailsPanel company={company} t={t} />
        </>
      )}
      {panel === 'access' && (
        <AccessPanel company={company} t={t} locale={locale} account={account} onChanged={onChanged} />
      )}
      {panel === 'numbering' && <NumberingPanel company={company} t={t} />}
      {panel === 'bank' && <BankAccountsPanel company={company} t={t} />}
    </Shell>
  );
}
